import {
  ARROW_DOWN_KEY,
  ARROW_LEFT_KEY,
  ARROW_RIGHT_KEY,
  ARROW_UP_KEY,
} from '@wix/music-shared/dist/src/utils/keyboard/key-codes';
import {durationToAccString} from '@wix/music-shared/dist/src/utils/time/time';
import assert from 'assert';
import classNames from 'classnames';
import {autobind} from 'core-decorators';
import PropTypes from 'prop-types';
import React from 'react';
import {withTranslation} from 'react-i18next';
import {AnyToDo} from '../../../../typings/todo';
import {getAngle} from '../../../services/utils/getAngle/getAngle';
import styles from './ProgressIndicator.scss';

const REWIND_AMOUNT = 5;

class ProgressIndicatorComponent extends React.Component<AnyToDo, AnyToDo> {
  static propTypes = {
    t: PropTypes.func,
    progress: PropTypes.number,
    duration: PropTypes.number,
    className: PropTypes.string,
    onProgressChange: PropTypes.func,
  };
  private _progressIndicatorWrapperEl: HTMLDivElement | null;

  constructor(props) {
    super(props);

    this.state = {
      draggingProgress: null,
    };

    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
  }

  render() {
    const {t, duration, progress = 0, className: externalClassName} = this.props;

    const {draggingProgress} = this.state;
    assert(progress <= 1 && progress >= 0, 'Progress should be in range from 0 to 1');
    const className = classNames(styles.progressIndicator, externalClassName);
    const size = 110;
    const thickness = 5;
    const R = (size - thickness) / 2;
    const fullCircle = 2 * Math.PI * R;
    const offset = fullCircle * (1 - (draggingProgress || progress));

    const valueText = t('mgw.aria.controlBar.duration', {
      progress: durationToAccString(progress * duration),
      duration: durationToAccString(duration),
    });
    return (
      <div
        className={styles.progressIndicatorWrapper}
        tabIndex={0}
        onKeyDown={this.handleKeyDown}
        onMouseDown={this.handleMouseDown}
        onTouchStart={this.handleMouseDown}
        role="slider"
        aria-valuemin={0}
        aria-valuemax={duration}
        aria-valuenow={progress * duration}
        aria-valuetext={valueText}
        data-hook="indicator-progress"
        ref={(el) => (this._progressIndicatorWrapperEl = el)}
      >
        <svg className={className} width={size} height={size} viewBox="0 0 110 110">
          <circle cx="55" cy="55" r={R} fill="none" stroke="rgba(255,255,255,.4)" strokeWidth={thickness} />
          <circle
            data-hook="indicator-progress-circle"
            cx="55"
            cy="55"
            r={R}
            fill="none"
            stroke="rgba(255,255,255,1)"
            strokeWidth={thickness}
            strokeDasharray={fullCircle}
            strokeDashoffset={offset}
            transform="rotate(-90 55 55)"
          />
        </svg>
      </div>
    );
  }

  componentDidMount() {
    document.body.addEventListener('mouseup', this.handleMouseUp);
    document.body.addEventListener('mousemove', this.handleMouseMove);
    document.body.addEventListener('touchend', this.handleMouseUp);
    document.body.addEventListener('touchmove', this.handleMouseMove);
  }

  componentWillUnmount() {
    document.body.removeEventListener('mouseup', this.handleMouseUp);
    document.body.removeEventListener('mousemove', this.handleMouseMove);
    document.body.removeEventListener('touchend', this.handleMouseUp);
    document.body.removeEventListener('touchmove', this.handleMouseMove);
  }

  handleKeyDown(event) {
    const {keyCode} = event;

    event.preventDefault();

    if (keyCode === ARROW_LEFT_KEY || keyCode === ARROW_DOWN_KEY) {
      this.rewindBackward();
    } else if (keyCode === ARROW_RIGHT_KEY || keyCode === ARROW_UP_KEY) {
      this.rewindForward();
    }
  }

  rewindBackward() {
    const {progress = 0, duration, onProgressChange} = this.props;
    const progressChange = progress - REWIND_AMOUNT / duration;
    onProgressChange(Math.max(progressChange, 0));
  }

  rewindForward() {
    const {progress = 0, duration, onProgressChange} = this.props;
    const progressChange = progress + REWIND_AMOUNT / duration;
    onProgressChange(Math.min(progressChange, 1));
  }

  handleMouseDown(event) {
    this.setState({
      draggingProgress: this.getAngle(event) / 2,
    });
  }

  handleMouseMove(event) {
    const {draggingProgress} = this.state;

    if (draggingProgress === null) {
      return;
    }

    event.preventDefault();

    this.setState({
      draggingProgress: this.getAngle(event) / 2,
    });
  }

  handleMouseUp(event) {
    const {draggingProgress} = this.state;
    const {onProgressChange} = this.props;

    if (draggingProgress === null) {
      return;
    }

    onProgressChange(this.getAngle(event) / 2);

    this.setState({
      draggingProgress: null,
    });
  }

  getAngle(event) {
    if (!this._progressIndicatorWrapperEl) {
      throw new Error('no element');
    }
    const rect = this._progressIndicatorWrapperEl.getBoundingClientRect();

    if (event.changedTouches && event.changedTouches[0]) {
      event = event.changedTouches[0];
    } else if (event.touches && event.touches[0]) {
      event = event.touches[0];
    }

    return getAngle({
      width: rect.width,
      height: rect.height,
      targetX: event.clientX - rect.left,
      targetY: event.clientY - rect.top,
    });
  }
}

export const ProgressIndicator = withTranslation()(ProgressIndicatorComponent);
